import { useEffect } from "react";
import {
  AUTO_PAR_NIVEAU,
  BASE_WEIGHT,
  COMPARAISON_SEUIL,
  GRADIENTS,
  GROUPS,
  HOUR_STEPS,
  LEVEL_WEIGHTS,
  MASTERY_AXES,
  MASTERY_LEVELS,
  MASTERY_MAX_POINTS,
  MIX_WEIGHT,
  SCORE_MAX,
  SCORE_MIN,
  TIME_COST_TASKS,
  GESTION_TASKS,
  scoringVersion,
} from "@riseafrica/scoring";
import { REFERENTIEL } from "../content/referentiel";

/**
 * Page méthode publique (ticket T0e).
 *
 * Critère d'acceptation : **un lecteur extérieur peut recalculer un indice à
 * la main depuis cette page.** Tout ce qui entre dans le calcul y figure —
 * bases, poids, formule, bornes, seuils, taux du volet 2, barème du volet 3 —
 * avec les valeurs importées du module de calcul lui-même. Elles ne peuvent
 * donc pas diverger de ce que le service applique : recopier ces nombres à la
 * main dans du texte aurait garanti l'inverse au premier changement de barème.
 */
export function Methode() {
  useEffect(() => {
    document.title = "Méthode de calcul — Diagnostic d'exposition à l'IA | STRATERA";
  }, []);

  const groupes = Object.entries(GROUPS).sort((a, b) => b[1].base - a[1].base);

  return (
    <section id="contenu">
      <div className="shell narrow app">
        <div className="kicker">La méthode, version {scoringVersion}</div>
        <h1 style={{ marginTop: "6px" }}>Comment l'indice est calculé</h1>
        <p className="lede">
          Tout ce qui entre dans le calcul est sur cette page. Vous pouvez refaire l'opération à la
          main, et vous pouvez la contester. C'est le sens de « un score que nous assumons de rendre
          public ».
        </p>

        {/*
          Les mots, avant la formule — 26/08/2026.

          Le produit emploie « exposition », « haute exposition », « automatiser »
          et « systématiser » dans son interface, dans son rapport et dans son
          attestation. Aucun n'était défini nulle part. Quelqu'un lisait « votre
          indice d'exposition est de 62 » et devait deviner ce que le mot
          recouvre — sur un chiffre qu'on lui demande justement de prendre au
          sérieux.

          Un lexique du vocabulaire de l'IA en général avait été envisagé, et
          écarté : il vieillirait contre nous, sur le site dont l'argument est la
          vérifiabilité, et il servirait un curieux plutôt qu'un acheteur.
          Celui-ci ne périme pas, parce que ces mots-là sont les nôtres et que
          nous en portons la définition.

          Il est placé **avant** la formule et non en annexe : on arrive ici
          depuis un écran de résultat, avec un mot en tête, pas avec une envie
          d'arithmétique.
        */}
        <div className="block" id="mots">
          <h2>Les mots employés ici</h2>
          <p className="fine">
            Cinq termes reviennent dans le diagnostic, le rapport et l'attestation. Ils ont un sens
            précis, et ce n'est pas toujours celui de l'usage courant.
          </p>
          <dl className="lexique">
            <dt>Exposition</dt>
            <dd>
              La part de vos tâches qu'une IA générative réalise <b>déjà</b>, pondérée par le temps
              que vous y passez. Ce n'est ni une probabilité de perdre son emploi, ni une prévision :
              c'est une mesure de ce qui est faisable aujourd'hui. Un indice élevé dit que le contenu
              du poste va changer, pas que le poste disparaît — la distinction est reprise au
              point 10.
            </dd>
            <dt>Haute exposition (une tâche)</dt>
            <dd>
              Une tâche classée <b>AUTOMATISABLE</b>, c'est-à-dire largement réalisable aujourd'hui
              par une IA générative. Elle pèse {LEVEL_WEIGHTS.high} dans la formule, contre{" "}
              {LEVEL_WEIGHTS.mid} pour une tâche partielle et {LEVEL_WEIGHTS.low} pour une tâche qui
              résiste.
            </dd>
            <dt>Automatiser</dt>
            <dd>
              Faire exécuter une tâche par un outil, à votre place, sur votre déclenchement. Le temps
              gagné vous revient. C'est ce que décrivent les automatisations du rapport : chacune dit
              quand elle se déclenche, tient en trois étapes, et chiffre le temps qu'elle rend — ou dit
              qu'il ne se chiffre pas.
            </dd>
            <dt>Systématiser</dt>
            <dd>
              Rendre une tâche reproductible <b>sans vous</b> — par une procédure écrite, un modèle
              de document, une règle que quelqu'un d'autre peut suivre. Ce n'est pas la même chose
              qu'automatiser : on peut systématiser sans aucun outil. C'est ce qui permet à une
              activité de tenir quand son fondateur n'est pas là, et c'est pour cela que la section
              de repositionnement d'un indépendant y revient.
            </dd>
            <dt>Assistant conversationnel</dt>
            <dd>
              Un outil auquel on écrit en français et qui répond en français. Le rapport ne nomme
              aucune marque, délibérément : elles changent plus vite que lui. Les outils du moment
              sont sur une page à part, datée, révisée chaque mois.
              {/*
                Le lien sort de la phrase — 26/08/2026.

                Pris dans le texte suivant, il faisait dix-neuf pixels de haut
                là où quarante-quatre sont exigés au pouce : la sonde de mise en
                page l'a refusé sur petit téléphone et sur tablette. `.lien-cible`
                porte la hauteur, mais sa note dit aussi pourquoi elle veut sa
                propre ligne — une boîte de quarante-quatre pixels au milieu
                d'une phrase déforme l'interligne autour d'elle.
              */}
              <p style={{ margin: "4px 0 0" }}>
                <a className="lien-cible" href="/outils">
                  Voir les outils du moment
                </a>
              </p>
            </dd>
          </dl>
        </div>

        <div className="block">
          <h2>1. La formule</h2>
          <pre className="formule">
            {`mix    = Σ ( poids(niveau de la tâche) × heures déclarées ) / Σ heures déclarées
indice = arrondi( base(groupe) × ${BASE_WEIGHT.toString().replace(".", ",")} + mix × ${MIX_WEIGHT.toString().replace(".", ",")} )
indice = borné entre ${SCORE_MIN} et ${SCORE_MAX}`}
          </pre>
          <p className="fine">
            En mode CV, aucune heure n'est déclarée : toutes les tâches pèsent 1, et le mix est la
            moyenne simple des poids.
          </p>
        </div>

        <div className="block">
          <h2>2. Les poids par niveau de tâche</h2>
          <table className="tab">
            <thead>
              <tr>
                <th>Niveau</th>
                <th>Ce qu'il signifie</th>
                <th>Poids</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>AUTOMATISABLE</td>
                <td>Largement réalisable aujourd'hui par une IA générative</td>
                <td>{LEVEL_WEIGHTS.high}</td>
              </tr>
              <tr>
                <td>PARTIEL</td>
                <td>Partiellement automatisable, arbitrage humain nécessaire</td>
                <td>{LEVEL_WEIGHTS.mid}</td>
              </tr>
              <tr>
                <td>RÉSISTE</td>
                <td>
                  Présence physique, geste technique, responsabilité engagée ou relation humaine
                  directe
                </td>
                <td>{LEVEL_WEIGHTS.low}</td>
              </tr>
            </tbody>
          </table>
          <p className="fine">
            Un niveau absent ou non reconnu vaut PARTIEL. Les temps proposés à la saisie sont{" "}
            {HOUR_STEPS.map((p) => p.libelle).join(", ")} — soit{" "}
            {HOUR_STEPS.map((p) => p.heures).join(", ").replace(/\./g, ",")} heures par semaine.
          </p>
        </div>

        <div className="block">
          <h2>3. Les onze bases d'exposition</h2>
          <p>
            Elles sont <b>dérivées</b> de l'indice d'exposition professionnelle à l'IA générative de
            l'Organisation internationale du Travail, qui publie un score entre 0 et 1 pour chacune
            des 427 professions de la classification internationale CITP-08.
          </p>
          <table className="tab">
            <thead>
              <tr>
                <th>Groupe</th>
                <th>Professions CITP-08</th>
                <th>Moyenne OIT</th>
                <th>Base</th>
              </tr>
            </thead>
            <tbody>
              {groupes.map(([code, groupe]) => {
                const detail = REFERENTIEL.groupes[code as keyof typeof REFERENTIEL.groupes];
                return (
                  <tr key={code}>
                    <td>{groupe.label}</td>
                    <td>{detail?.professions ?? "—"}</td>
                    <td>
                      {detail ? detail.moyenne.toFixed(4).replace(".", ",") : "—"}
                      {detail && !detail.pondere ? (
                        <span className="fine"> non pondérée</span>
                      ) : null}
                    </td>
                    <td>
                      <b>{groupe.base}</b>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p>
            Chaque profession est affectée à un et un seul groupe. La moyenne des professions du
            groupe, <b>pondérée par l'emploi</b>, est ramenée sur l'échelle 0–100 par :
          </p>
          <pre className="formule">
            {`base = arrondi( 100 × ( moyenne − ${REFERENTIEL.muMin.toString().replace(".", ",")} ) / ( ${REFERENTIEL.muMax.toString().replace(".", ",")} − ${REFERENTIEL.muMin.toString().replace(".", ",")} ) )`}
          </pre>
          <p className="fine">
            {REFERENTIEL.muMin.toString().replace(".", ",")} et{" "}
            {REFERENTIEL.muMax.toString().replace(".", ",")} sont les scores de la profession la
            moins exposée et de la plus exposée du référentiel de l'OIT. Une base de 0 correspond
            donc au métier le moins exposé du monde du travail selon cette source, une base de 100 au
            plus exposé. Aucun paramètre libre, aucun ajustement après coup.
          </p>

          <div className="refer">
            <b>Source et limites</b>
            Gmyrek, P. ; Berg, J. ; Kamiński, K. ; Konopczyński, F. ; Ładna, A. ; Nafradi, B. ;
            Rosłaniec, K. ; Troszyński, M. 2025. « Generative AI and jobs: a refined global index of
            occupational exposure », ILO Working Paper 140, Genève, Organisation internationale du
            Travail. Licence CC BY 4.0.
            <span className="fine" style={{ display: "block", marginTop: "8px" }}>
              Notre travail est une <b>adaptation</b> : les professions ont été regroupées et les
              scores ramenés sur une autre échelle. Elle n'est ni validée, ni approuvée, ni publiée
              par l'OIT. Deux limites que nous ne corrigeons pas et que nous préférons écrire : la
              pondération par l'emploi repose sur des effectifs <b>ivoiriens</b> (ILOSTAT, 2019),
              faute de répartition comparable pour toute la zone — la direction du déplacement y
              est vraie partout, son ampleur est ivoirienne ; et l'indice de l'OIT est établi dans
              un contexte à haut revenu et à forte pénétration numérique, ce qui majore
              probablement l'exposition réelle ici.
            </span>
            {/*
              Les trois fichiers, et non leur description — 02/09/2026.

              L'accueil promettait « nous publions le tableau qui relie chaque
              profession à l'un de nos onze groupes ». Il vivait au dépôt, et le
              site n'en servait rien : la promesse la plus vérifiable du produit
              était la seule qu'on ne pouvait pas vérifier.

              Ils sont copiés depuis `data/oit/` à chaque construction, donc
              c'est bien le calcul appliqué qui est servi.
            */}
            <span className="fine" style={{ display: "block", marginTop: "8px" }}>
              <b>Refaire le calcul.</b> Les trois fichiers que le calcul lit sont servis tels
              quels, avec le dossier qui donne la provenance de chacun, les familles écartées et
              les hypothèses.
            </span>
            {/*
              Quatre liens, quatre lignes — 02/09/2026.

              Première rédaction : les quatre pris dans la phrase ci-dessus. La
              sonde de mise en page les a refusés sur les trois largeurs —
              trente-trois pixels de haut là où quarante-quatre sont exigés au
              pouce. C'est la leçon déjà écrite deux fois dans ce dépôt, dont
              une sur cet écran même, six cents lignes plus haut : `.lien-cible`
              porte la hauteur, et une boîte de quarante-quatre pixels au milieu
              d'une phrase déforme l'interligne autour d'elle.
            */}
            {[
              ["ILO2025_scores_isco08_4d.csv", "Les 427 scores de l'OIT (CSV)"],
              ["citp08-vers-groupes.csv", "Le tableau de passage vers nos onze groupes (CSV)"],
              ["ilostat-emploi-civ-2019.csv", "Les effectifs qui pondèrent (CSV)"],
              ["SOURCE.md", "Le dossier de source (texte)"],
            ].map(([fichier, libelle]) => (
              <p key={fichier} style={{ margin: 0 }}>
                <a className="lien-cible" href={`/donnees/${fichier}`} download>
                  {libelle}
                </a>
              </p>
            ))}
          </div>
        </div>

        <div className="block">
          <h2>4. Les quatre niveaux affichés</h2>
          <table className="tab">
            <thead>
              <tr>
                <th>Indice</th>
                <th>Niveau</th>
              </tr>
            </thead>
            <tbody>
              {GRADIENTS.map((gradient, i) => (
                <tr key={gradient.niveau}>
                  <td>
                    {i === 0
                      ? `moins de ${gradient.max}`
                      : i === GRADIENTS.length - 1
                        ? `${GRADIENTS[i - 1].max} et plus`
                        : `${GRADIENTS[i - 1].max} à ${gradient.max - 1}`}
                  </td>
                  <td>{gradient.libelle}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="block">
          <h2>5. La comparaison entre confrères</h2>
          <p>
            Aucun rang n'est affiché avant d'avoir la population qui le justifie. Tant qu'un groupe
            professionnel compte moins de <b>{COMPARAISON_SEUIL} résultats enregistrés</b>, la
            comparaison n'est pas disponible et l'écran le dit. Au-delà, le rang est la part des
            scores du groupe strictement inférieurs au vôtre.
          </p>
          <p className="fine">
            Les résultats issus du mode métier et du mode CV ne sont jamais mélangés : les tâches n'y
            pèsent pas de la même façon.
          </p>
          <p>
            En dessous de ce seuil, l'écran affiche un <b>repère</b> et non un rang : le nombre de
            professions de votre groupe, parmi les 427 recensées par l'OIT, dont l'indice est
            inférieur au vôtre. Ces indices sont ceux de la source, ramenés sur notre échelle par la
            formule qui donne déjà les onze bases — aucun paramètre supplémentaire. Les données sont
            publiques et le calcul se refait&nbsp;: voir la section 1.
          </p>
          <p className="fine">
            Deux limites, dites franchement. Ce repère dénombre des <b>professions</b>, pas des
            personnes&nbsp;; il n'est pas la comparaison entre confrères et ne la remplace pas. Et
            votre indice est pondéré par les tâches et le temps que vous déclarez, quand celui de la
            source est un indice de profession&nbsp;: les deux vivent sur la même échelle, ce qui
            rend le rapprochement lisible, non identique. C'est aussi pourquoi il est exprimé en
            nombre de professions et jamais en pourcentage — quatre à deux cent trente-deux professions
            selon le groupe ne portent pas la précision qu'un pourcentage laisserait croire.
          </p>
        </div>

        {/*
          La reproductibilité de la mesure, dite publiquement — 25/08/2026.

          Le produit assume en interne qu'« au-delà de la durée de vie d'une
          entrée de cache, une nouvelle génération peut déplacer le score de
          quelques points ». C'est un arbitrage défendable, et il n'était écrit
          nulle part à l'usage des personnes mesurées — alors que le produit
          leur promet par ailleurs de comparer deux chiffres à trois mois.

          Une méthode publiée qui tairait sa propre variabilité serait moins
          crédible que pas de méthode publiée du tout : c'est exactement ce
          qu'un lecteur attentif viendrait chercher, et ne trouverait pas.
        */}
        <div className="block">
          <h2>Ce que vaut une seconde mesure</h2>
          <p>
            Le barème est déterministe&nbsp;: les mêmes tâches, aux mêmes niveaux, avec les mêmes
            heures, donnent toujours le même indice. Le calcul est fait par le code, jamais par le
            modèle, et il est écrit en section 1.
          </p>
          <p>
            Ce qui peut varier est <b>en amont</b>. La liste de vos tâches est proposée par un
            modèle de langage, et deux propositions successives pour un même métier ne découpent
            pas toujours le travail exactement de la même façon — l'une réunit ce que l'autre
            sépare. Nous conservons chaque liste pour éviter d'en produire une nouvelle à chaque
            visite, mais cette conservation a une durée&nbsp;: au-delà, une nouvelle génération
            peut déplacer votre indice de <b>quelques points</b> sans que votre travail ait changé.
          </p>
          <p className="fine">
            Conséquence pratique, et c'est la seule qui compte pour vous&nbsp;: à trois mois,
            regardez le <b>sens</b> de l'écart et son ampleur, pas le point près. Un mouvement de
            deux ou trois points ne dit rien&nbsp;; un mouvement de dix points dit quelque chose.
            Nous préférons l'écrire ici que de laisser croire à une précision que la mesure n'a
            pas — et c'est aussi pourquoi le rappel que vous posez dans votre agenda porte le même
            avertissement.
          </p>
        </div>

        <div className="block">
          <h2>6. Le coût du temps</h2>
          <table className="tab">
            <thead>
              <tr>
                <th>Tâche</th>
                <th>Part automatisable</th>
              </tr>
            </thead>
            <tbody>
              {TIME_COST_TASKS.map((tache) => (
                <tr key={tache.name}>
                  <td>{tache.name}</td>
                  <td>{tache.auto.toFixed(2).replace(".", ",")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <pre className="formule">
            {`heures récupérables par semaine = Σ ( heures par semaine × part automatisable )
heures récupérables par mois    = heures récupérables par semaine × 4,33
taux horaire                    = rémunération mensuelle nette / 173
valeur annuelle                 = heures récupérables par mois × taux horaire × 12`}
          </pre>
          <p className="fine">
            <b>Une semaine de 40 heures, cinq journées de 8.</b> C'est la semaine légale de la
            zone, et c'est ce que les 173 heures mensuelles encodent : 40 × 52 ÷ 12. Les journées
            affichées divisent par 8, et un mois en compte 22. La couverture du rapport payé porte
            les heures récupérables par semaine, calculées ainsi — jamais un chiffre écrit par le
            modèle.
          </p>
          <p className="fine">
            <b>Ces six taux sont les nôtres.</b> Contrairement aux bases du chapitre 3, ils ne sont
            dérivés d'aucune source publiée. Nous les publions au même titre que le reste, et c'est
            la prochaine dérivation à faire.
          </p>
          {/*
            La gestion de l'activité — T38, 24/09/2026. Publiée ici parce
            qu'elle entre dans le temps récupérable et sa valeur, et dite
            **hors indice** en toutes lettres : c'est la seule chose qu'un
            lecteur attentif pourrait craindre qu'elle déplace.
          */}
          <h3 style={{ marginTop: "22px" }}>Pour qui travaille à son compte : la gestion de l'activité</h3>
          <p>
            Une personne à son compte déclare en plus le temps qu'il lui faut pour faire tourner son
            affaire. Chaque tâche porte un niveau, comme une tâche du métier, et se chiffre avec les
            mêmes parts : {AUTO_PAR_NIVEAU.high.toFixed(2).replace(".", ",")} pour ce qui est
            largement automatisable, {AUTO_PAR_NIVEAU.mid.toFixed(2).replace(".", ",")} pour ce qui
            l'est en partie.
          </p>
          <table className="tab">
            <thead>
              <tr>
                <th>Tâche de gestion</th>
                <th>Niveau</th>
              </tr>
            </thead>
            <tbody>
              {GESTION_TASKS.map((tache) => (
                <tr key={tache.name}>
                  <td>{tache.name}</td>
                  <td>{tache.niveau === "high" ? "Largement automatisable" : "En partie"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="fine">
            <b>Ces heures n'entrent pas dans l'indice.</b> Il reste calculé sur les tâches du métier
            seules, avec la même formule pour tous : deux échelles selon le statut rendraient le
            baromètre incomparable à lui-même. Elles comptent dans le temps récupérable, dans sa
            valeur, et dans la partie du rapport qui dit ce que la personne n'aura pas à confier à un
            renfort — nommé par catégorie, jamais par un nom de marque ni de prestataire.
          </p>
        </div>

        <div className="block">
          <h2>7. Le niveau de maîtrise</h2>
          {/*
            Le nombre de questions et le total sont comptés, plus écrits — 27/08/2026.

            Le volet a doublé le 12/08 (huit axes, deux questions chacun) sans
            que cette page le suive : elle annonçait encore « huit questions »
            et « total sur 16 », et la dernière ligne du tableau calculait sa
            borne haute contre un 16 resté figé — produisant une plage
            décroissante (« 26 à 16 »), sur la page qui promet justement de
            pouvoir tout recalculer à la main. Comme pour le nombre affiché à
            l'accueil (voir le commentaire de Home.tsx), la source vient
            désormais de `@riseafrica/scoring` et ne peut plus diverger d'elle.
          */}
          <p>
            {MASTERY_AXES.length} axes, deux questions chacun, trois réponses possibles notées 0,
            1 ou 2. Total sur {MASTERY_MAX_POINTS}.
          </p>
          <table className="tab">
            <thead>
              <tr>
                <th>Points</th>
                <th>Niveau</th>
              </tr>
            </thead>
            <tbody>
              {MASTERY_LEVELS.map((niveau, i) => (
                <tr key={niveau.niveau}>
                  <td>
                    {i === 0
                      ? `0 à ${niveau.max - 1}`
                      : i === MASTERY_LEVELS.length - 1
                        ? `${MASTERY_LEVELS[i - 1].max} à ${MASTERY_MAX_POINTS}`
                        : `${MASTERY_LEVELS[i - 1].max} à ${niveau.max - 1}`}
                  </td>
                  <td>{niveau.libelle}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="fine">
            C'est le seul volet sur lequel une attestation peut porter : ses réponses sont notées sur
            un barème fermé. L'indice d'exposition, lui, mesure ce que vous déclarez — utile pour
            vous, sans valeur comme preuve pour un tiers.
          </p>
        </div>

        {/*
          L'axe dirigeant, publié au même titre que le reste.

          Ce chapitre existe parce que l'écran de résultat dirigeant affirme
          « la méthode complète est publiée sur ce site ». Tant qu'il manquait,
          cette phrase était fausse — et une promesse de transparence non tenue
          coûte davantage que l'opacité qu'elle prétend corriger.

          Il dit aussi, sans le contourner, ce que cet axe a de plus faible : les
          onze coefficients ne sont dérivés d'aucune source. C'est précisément la
          raison pour laquelle ils ne sont plus sur le chemin principal.
        */}
        <div className="block">
          <h2>7 bis. Ce que les tâches automatisables occupent dans une entreprise</h2>
          <p>
            L'axe dirigeant mesure la même chose à l'échelle d'une entreprise. Le chiffre mis en
            avant est un <b>équivalent temps plein</b>, et il ne repose sur aucune hypothèse :
          </p>
          <pre className="formule">
            {`heures automatisables  = Σ ( heures par semaine × part automatisable du poste )
équivalent temps plein = heures automatisables / 40`}
          </pre>
          <p>
            Le montant en francs vient ensuite, et son exactitude dépend de ce que vous acceptez de
            déclarer. Quatre provenances possibles, de la plus sûre à la plus faible — l'écran dit
            toujours laquelle s'applique à votre résultat :
          </p>
          <table className="tab">
            <thead>
              <tr>
                <th>Ce que vous déclarez</th>
                <th>Hypothèse</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Votre coût horaire chargé</td>
                <td>aucune</td>
              </tr>
              <tr>
                <td>Votre masse salariale mensuelle chargée</td>
                <td>aucune — une division par l'effectif et 173 heures</td>
              </tr>
              <tr>
                <td>Votre salaire mensuel moyen</td>
                <td>une : le taux de charges patronales</td>
              </tr>
              <tr>
                <td>Rien — estimation par secteur</td>
                <td>onze coefficients de masse salariale, non sourcés</td>
              </tr>
            </tbody>
          </table>
          <pre className="formule">
            {`coût horaire      = masse salariale chargée / (effectif × 173)
perte mensuelle   = Σ ( heures par semaine × 4,33 × coût horaire × part automatisable )
                    arrondie au multiple de 5 000 F INFÉRIEUR`}
          </pre>
          <p className="fine">
            <b>Les onze coefficients par secteur ne sont dérivés d'aucune source publiée.</b> Ils
            vont de 15 % pour le commerce à 50 % pour l'enseignement, et produisent à eux seuls des
            coûts horaires dans un rapport de plus de trois. C'est la raison pour laquelle ils ne
            servent qu'en dernier recours, quand rien n'a été déclaré — et dans ce cas la tranche de
            chiffre d'affaires la plus basse est retenue, ce qui sous-estime le montant plutôt que
            de le deviner. Nous cherchons une source publique pour les remplacer ; tant qu'elle
            n'est pas trouvée, ce sont des ordres de grandeur, et rien d'autre.
          </p>
          <p className="fine">
            Une dernière précision, parce qu'elle se conteste à juste titre : ce montant est un{" "}
            <b>salaire dépensé</b>, pas une perte. Quelqu'un a fait le travail. C'est pourquoi le
            chiffre mis en avant est un temps, et non une somme.
          </p>
        </div>

        <div className="block">
          <h2>8. Le journal des versions</h2>
          <table className="tab">
            <thead>
              <tr>
                <th>Version</th>
                <th>Date</th>
                <th>Ce qui change</th>
              </tr>
            </thead>
            <tbody>
              {REFERENTIEL.versions.map((version) => (
                <tr key={version.version}>
                  <td>{version.version}</td>
                  <td>{version.date}</td>
                  <td>{version.texte}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="fine">
            Chaque résultat enregistré porte la version du barème qui l'a produit. Un indice daté
            reste donc reconstituable, même après une révision.
          </p>
        </div>

        <div className="block">
          <h2>9. Un exemple, à refaire à la main</h2>
          <p>
            Une comptable déclare 8 h de saisie de pièces (AUTOMATISABLE), 4,5 h de reporting
            (AUTOMATISABLE), 4,5 h de déclarations fiscales (PARTIEL) et 1,5 h de relation avec le
            commissaire aux comptes (RÉSISTE).
          </p>
          <pre className="formule">
            {`mix    = (100×8 + 100×4,5 + 55×4,5 + 15×1,5) / (8 + 4,5 + 4,5 + 1,5)
       = (800 + 450 + 247,5 + 22,5) / 18,5
       = 1520 / 18,5 = 82,2

indice = arrondi( ${GROUPS.COMPTA.base} × ${BASE_WEIGHT.toString().replace(".", ",")} + 82,2 × ${MIX_WEIGHT.toString().replace(".", ",")} )
       = arrondi( ${(GROUPS.COMPTA.base * BASE_WEIGHT).toFixed(2).replace(".", ",")} + ${(82.2 * MIX_WEIGHT).toFixed(2).replace(".", ",")} )
       = ${Math.round(GROUPS.COMPTA.base * BASE_WEIGHT + 82.2 * MIX_WEIGHT)}`}
          </pre>
          <p className="fine">
            Si vous obtenez un autre nombre que celui affiché par le service pour ce profil, c'est
            nous qui avons tort. Écrivez-nous.
          </p>
        </div>

        <div className="block">
          <h2>10. Ce que cet indice ne fait pas</h2>
          <ul className="listy">
            <li>
              <span>
                Il ne prédit aucun licenciement. Il mesure l'exposition de vos tâches, pas la
                décision de votre employeur ni celle de vos clients.
              </span>
            </li>
            <li>
              <span>
                Il n'est pas difficile à manipuler — c'est l'inverse. Le temps que vous déclarez est
                l'entrée la plus facile à orienter, et l'écran vous apprend la correspondance en
                affichant les étiquettes. Il vous est utile ; il n'a aucune valeur comme preuve
                fournie à un tiers.
              </span>
            </li>
            <li>
              <span>
                Il ne dit rien de votre valeur professionnelle. Un métier très exposé n'est pas un
                métier de moindre valeur : c'est un métier dont le contenu va changer.
              </span>
            </li>
            <li>
              <span>
                Il repose sur {REFERENTIEL.metiersValides} métiers relus et sur un modèle pour les
                autres. Quand votre métier vient du modèle, ses tâches n'ont pas été relues par un
                humain.
              </span>
            </li>
          </ul>
        </div>

        <p className="fine" style={{ marginTop: "24px" }}>
          Une erreur dans cette page ? Elle vaut correction publique et mention au journal des
          versions.
        </p>
      </div>
    </section>
  );
}
